import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageBanner from "@/components/PageBanner";
import SectionTitle from "@/components/SectionTitle";
import { IMAGES, COMPANY } from "@/lib/content";
import { Cookie, Shield, BarChart3, Settings2 } from "lucide-react";

const COOKIE_CATEGORIES = [
  {
    icon: Shield,
    title: "Strictly Necessary Cookies",
    text:
      "These cookies are essential for the website to function properly. They enable core features such as page navigation, secure areas, and remembering your cookie preferences. The website cannot function without these cookies, and they can only be disabled by changing your browser settings.",
    always: true,
  },
  {
    icon: BarChart3,
    title: "Analytics & Performance Cookies",
    text:
      "These cookies help us understand how visitors interact with the website by collecting and reporting information anonymously (pages visited, time on site, basic device information). They help us improve the site and the quality of our content. They are only set with your explicit consent.",
    always: false,
  },
  {
    icon: Settings2,
    title: "Functionality Cookies",
    text:
      "These cookies allow the website to remember choices you make (such as your preferred language or region) and provide enhanced, more personal features. They are only set with your explicit consent.",
    always: false,
  },
];

const CookiesPolicy = () => {
  return (
    <div className="min-h-screen flex flex-col bg-abayle-ivory">
      <Navbar />
      <main>
        <PageBanner
          eyebrow="Legal"
          title="Cookies Policy"
          subtitle="How Abayle Logistics and Trading uses cookies and similar technologies on this website."
          image={IMAGES.aboutPort}
        />

        {/* Intro */}
        <section className="bg-white" data-testid="cookies-intro">
          <div className="container-abayle py-14 sm:py-20 lg:py-24 grid lg:grid-cols-5 gap-8 sm:gap-14 items-start">
            <div className="lg:col-span-2">
              <SectionTitle eyebrow="Overview" title="What Are Cookies?" />
            </div>
            <div className="lg:col-span-3 text-abayle-charcoal/85 text-sm sm:text-base lg:text-lg leading-relaxed space-y-4">
              <p>
                Cookies are small text files that are placed on your device (computer, tablet, or
                mobile) when you visit a website. They are widely used to make websites function
                more efficiently and to provide information to the site&apos;s owners.
              </p>
              <p>
                This Cookies Policy explains what cookies are, which cookies we use on the Abayle
                Logistics and Trading website, why we use them, and your choices regarding their
                use.
              </p>
              <p className="text-[13px] text-abayle-charcoal/60">
                Last updated: {new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}
              </p>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="bg-abayle-ivory" data-testid="cookies-categories">
          <div className="container-abayle py-14 sm:py-20 lg:py-28">
            <SectionTitle
              centered
              eyebrow="Categories"
              title="Types of Cookies We Use"
              subtitle="We use a minimal, transparent set of cookies. You can accept or reject non-essential cookies at any time through the banner at the bottom of your screen."
            />
            <div className="grid md:grid-cols-3 gap-5 sm:gap-6">
              {COOKIE_CATEGORIES.map((c, idx) => {
                const Icon = c.icon;
                return (
                  <div key={c.title} className="card-abayle" data-testid={`cookies-category-${idx}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-12 w-12 rounded-md bg-abayle-green/5 border border-abayle-green/15 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-abayle-green" />
                      </div>
                      <span
                        className={`text-[10px] font-bold tracking-[0.2em] uppercase px-2.5 py-1 rounded-full border ${
                          c.always
                            ? "text-abayle-green border-abayle-green/30 bg-abayle-green/5"
                            : "text-abayle-gold border-abayle-gold/40 bg-abayle-gold/5"
                        }`}
                      >
                        {c.always ? "Always Active" : "Consent Required"}
                      </span>
                    </div>
                    <h3 className="text-abayle-green font-bold text-lg mb-3 leading-snug">
                      {c.title}
                    </h3>
                    <p className="text-abayle-charcoal/75 text-[14px] sm:text-[15px] leading-relaxed">
                      {c.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Managing cookies */}
        <section className="bg-white" data-testid="cookies-managing">
          <div className="container-abayle py-14 sm:py-20 lg:py-24 grid lg:grid-cols-5 gap-8 sm:gap-14 items-start">
            <div className="lg:col-span-2">
              <SectionTitle eyebrow="Your Choices" title="Managing Your Cookies" />
            </div>
            <div className="lg:col-span-3 text-abayle-charcoal/85 text-sm sm:text-base lg:text-lg leading-relaxed space-y-4">
              <p>
                When you first visit our site, a cookie banner is displayed giving you the option
                to <strong>Accept</strong> or <strong>Reject</strong> non-essential cookies. Your
                choice is remembered in your browser. You can change your preference at any time by
                clearing your browser storage for this site, after which the banner will appear
                again on your next visit.
              </p>
              <p>
                Most web browsers also allow you to control cookies directly through their
                settings. You can typically find these options under &quot;Privacy&quot; or
                &quot;Security&quot; in your browser preferences. Please note that disabling
                strictly necessary cookies may prevent parts of the website from working correctly.
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-[15px]">
                <li>
                  <a className="text-abayle-green hover:text-abayle-gold underline decoration-abayle-gold/50 underline-offset-4" href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noreferrer">Google Chrome</a>
                </li>
                <li>
                  <a className="text-abayle-green hover:text-abayle-gold underline decoration-abayle-gold/50 underline-offset-4" href="https://support.mozilla.org/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noreferrer">Mozilla Firefox</a>
                </li>
                <li>
                  <a className="text-abayle-green hover:text-abayle-gold underline decoration-abayle-gold/50 underline-offset-4" href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noreferrer">Apple Safari</a>
                </li>
                <li>
                  <a className="text-abayle-green hover:text-abayle-gold underline decoration-abayle-gold/50 underline-offset-4" href="https://support.microsoft.com/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noreferrer">Microsoft Edge</a>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Third parties + Contact */}
        <section className="bg-abayle-ivory" data-testid="cookies-thirdparty">
          <div className="container-abayle py-14 sm:py-20 lg:py-24 grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
            <div>
              <SectionTitle eyebrow="Third Parties" title="Third-Party Services" />
              <p className="text-abayle-charcoal/85 text-sm sm:text-base leading-relaxed">
                With your consent, we may use third-party analytics services to help us understand
                site usage. These providers may set their own cookies subject to their own privacy
                policies. If you reject non-essential cookies, we will not load these services on
                your device.
              </p>
            </div>
            <div>
              <SectionTitle eyebrow="Questions?" title="Contact Us" />
              <p className="text-abayle-charcoal/85 text-sm sm:text-base leading-relaxed">
                If you have any questions about this Cookies Policy or our use of cookies, please
                contact {COMPANY.name} at{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-abayle-green hover:text-abayle-gold underline decoration-abayle-gold/50 underline-offset-4 font-semibold"
                  data-testid="cookies-contact-email"
                >
                  {COMPANY.email}
                </a>
                , or visit our{" "}
                <Link
                  to="/contact"
                  className="text-abayle-green hover:text-abayle-gold underline decoration-abayle-gold/50 underline-offset-4 font-semibold"
                >
                  contact page
                </Link>
                .
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CookiesPolicy;
